import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../shared/components/core-ui/button/button.component';
import { LoadingData } from '../../../ui/LoadingData';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import {
  BulkUpdate,
  BulkUpdateOutcomeRecord,
  ChannelManagerSyncStatus
} from '../../../shared/models/unit-types.model';
import {
  BulkActionsPage,
  formatActionType,
  getBulkActions,
  toOutcomeRecords
} from '../../bulk-actions.service';

const PAGE_SIZE = 20;

const RENTALS_UNITED_STATUS_LABELS: Record<ChannelManagerSyncStatus, string> = {
  ok: 'Synced',
  failed: 'Failed',
  skipped: 'Not synced'
};

const formatWhen = (value: number | string | undefined) => {
  if (!value) return '—';
  const date = new Date(value as any);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const StatusPill: React.FC<{ tone: 'ok' | 'bad' | 'muted'; children: React.ReactNode }> = ({
  tone,
  children
}) => (
  <span
    className={
      'rounded px-2 py-0.5 text-[1.1rem] font-gilroy-medium uppercase tracking-[0.05em] ' +
      (tone === 'bad'
        ? 'bg-red-50 text-red-700'
        : tone === 'ok'
        ? 'bg-green-50 text-green-700'
        : 'bg-gray-100 text-gray-500')
    }
  >
    {children}
  </span>
);

/**
 * History of every bulk action taken on units, read from the bulkUpdates collection.
 * Each row expands to name the units that failed, on the Birdnest side and on the
 * Rentals United side separately.
 */
export const BulkActionsListContainer: React.FC = () => {
  const [bulkUpdates, setBulkUpdates] = useState<BulkUpdate[]>([]);
  const [count, setCount] = useState<number>(0);
  const [offset, setOffset] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorInfo>();
  const [expandedId, setExpandedId] = useState<string>();

  const fetchPage = useCallback((page: number) => {
    setIsLoading(true);
    setError(undefined);
    getBulkActions(page, PAGE_SIZE)
      .then((response: BulkActionsPage | ErrorInfo) => {
        if (isErrorInfo(response)) {
          setError(response);
          setBulkUpdates([]);
          setCount(0);
          return;
        }
        setBulkUpdates(response.bulkUpdates);
        setCount(response.count);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchPage(offset);
    setExpandedId(undefined);
  }, [offset, fetchPage]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(count / PAGE_SIZE)), [count]);

  return (
    <div className="p-5 font-gilroy-regular">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
        <div>
          <div className="font-semibold font-gilroy-medium text-primary-500 text-3xl">
            Bulk Actions History
          </div>
          <div className="mt-1 text-[1.3rem] text-gray-500">
            Every bulk update run on units, newest first. Open a run to see which units failed.
          </div>
        </div>
        <Button
          onClick={() => fetchPage(offset)}
          type={ButtonType.primaryInverted}
          size={ButtonSize.medium}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <LoadingData />
      ) : error ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-[1.4rem] text-gray-500">
          Could not load the bulk actions history.
        </div>
      ) : bulkUpdates.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-[1.4rem] text-gray-500">
          No bulk actions have been run yet.
        </div>
      ) : (
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <div className="hidden md:flex items-center gap-4 px-4 py-3 border-b border-gray-200 bg-gray-50 text-[1.2rem] font-gilroy-medium uppercase tracking-[0.06em] text-gray-500">
            <div className="w-48 shrink-0">When</div>
            <div className="w-44 shrink-0">Run by</div>
            <div className="flex-1 min-w-0">Action</div>
            <div className="w-24 shrink-0 text-center">Units</div>
            <div className="w-40 shrink-0">Birdnest</div>
            <div className="w-56 shrink-0">Rentals United</div>
            <div className="w-24 shrink-0" />
          </div>

          {bulkUpdates.map((bulkUpdate) => {
            const id = String(bulkUpdate.id);
            const records = toOutcomeRecords(bulkUpdate);
            const birdnestFailed = (bulkUpdate.errorRecords || []).length;
            const birdnestOk = (bulkUpdate.successRecords || []).length;
            const ruFailed = records.filter((r) => r.rentalsUnited === 'failed').length;
            const ruOk = records.filter((r) => r.rentalsUnited === 'ok').length;
            const ruSkipped = records.filter((r) => r.rentalsUnited === 'skipped').length;
            const ruUnknown = records.filter((r) => !r.rentalsUnited).length;
            const problems = records.filter(
              (record) => record.birdnest === 'failed' || record.rentalsUnited === 'failed'
            );
            const actionLabel = formatActionType(bulkUpdate.actionType);
            const isExpanded = expandedId === id;

            return (
              <div key={id} className="border-b border-gray-200 last:border-b-0">
                <button
                  type="button"
                  onClick={() => setExpandedId(isExpanded ? undefined : id)}
                  className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 w-full px-4 py-3 bg-transparent text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="w-48 shrink-0 text-[1.4rem] font-gilroy-medium text-gray-900">
                    {formatWhen(bulkUpdate.createdAt)}
                  </div>
                  <div className="w-44 shrink-0 text-[1.4rem] text-gray-700 truncate">
                    {bulkUpdate.updateByUser || '—'}
                  </div>
                  <div className="flex-1 min-w-0">
                    {actionLabel ? (
                      <>
                        <div className="text-[1.4rem] font-gilroy-medium text-gray-900 truncate">
                          {actionLabel}
                        </div>
                        {bulkUpdate.actionDescription && (
                          <div className="text-[1.2rem] text-gray-500 truncate">
                            {bulkUpdate.actionDescription}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="text-[1.3rem] text-gray-400 italic">not recorded</span>
                    )}
                  </div>
                  <div className="w-24 shrink-0 md:text-center text-[1.4rem] text-gray-700">
                    {bulkUpdate.updatedItemsCount ?? records.length}
                  </div>
                  <div className="w-40 shrink-0 flex items-center gap-2">
                    <StatusPill tone={birdnestFailed > 0 ? 'bad' : 'ok'}>
                      {birdnestOk} ok
                    </StatusPill>
                    {birdnestFailed > 0 && <StatusPill tone="bad">{birdnestFailed} failed</StatusPill>}
                  </div>
                  <div className="w-56 shrink-0 flex items-center gap-2 flex-wrap">
                    {ruUnknown === records.length && records.length > 0 ? (
                      <StatusPill tone="muted">not tracked</StatusPill>
                    ) : (
                      <>
                        <StatusPill tone={ruFailed > 0 ? 'bad' : 'ok'}>{ruOk} synced</StatusPill>
                        {ruFailed > 0 && <StatusPill tone="bad">{ruFailed} failed</StatusPill>}
                        {ruSkipped > 0 && <StatusPill tone="muted">{ruSkipped} not synced</StatusPill>}
                      </>
                    )}
                  </div>
                  <div className="w-24 shrink-0 text-[1.3rem] text-primary-500 font-gilroy-medium md:text-right">
                    {isExpanded ? 'Hide' : `Details${problems.length ? ` (${problems.length})` : ''}`}
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 bg-gray-50">
                    {records.length === 0 ? (
                      <div className="py-3 text-[1.3rem] text-gray-500">
                        This run recorded no per-unit results.
                      </div>
                    ) : (
                      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                        {records.map((record, index) => (
                          <div
                            key={`${record.unitId}-${index}`}
                            className="px-4 py-2 border-b border-gray-200 last:border-b-0"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-[1.4rem] text-gray-900 truncate">
                                {record.unitName || `Unit ${record.unitId}`}
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <StatusPill tone={record.birdnest === 'ok' ? 'muted' : 'bad'}>
                                  BN {record.birdnest === 'ok' ? 'ok' : 'failed'}
                                </StatusPill>
                                <StatusPill
                                  tone={record.rentalsUnited === 'failed' ? 'bad' : 'muted'}
                                >
                                  RU{' '}
                                  {record.rentalsUnited
                                    ? RENTALS_UNITED_STATUS_LABELS[record.rentalsUnited]
                                    : 'not tracked'}
                                </StatusPill>
                              </div>
                            </div>
                            {record.errorMessage && (
                              <div className="mt-1 text-[1.2rem] leading-normal text-gray-500 break-words">
                                {record.errorMessage}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isLoading && !error && count > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-[1.3rem] text-gray-500">
            Page {offset} of {totalPages} · {count} run{count === 1 ? '' : 's'}
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setOffset((page) => Math.max(1, page - 1))}
              type={ButtonType.primaryInverted}
              size={ButtonSize.medium}
              disabled={offset <= 1}
            >
              Previous
            </Button>
            <Button
              onClick={() => setOffset((page) => Math.min(totalPages, page + 1))}
              type={ButtonType.primaryInverted}
              size={ButtonSize.medium}
              disabled={offset >= totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkActionsListContainer;
